import type { TransferEligibilityIssue, TransferEligibilityResult } from '@shared/calls/transfer-eligibility'
import { primaryTransferEligibilityIssue } from '@shared/calls/transfer-eligibility'

function TransferWarnIcon() {
  return (
    <svg className="h-6 w-6 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  )
}

export type TransferEligibilityChoice = 'cancel' | 'continue' | 'book_callback'

export function TransferEligibilityDialog({
  open,
  targetName,
  result,
  bookingCallback,
  onChoose,
}: {
  open: boolean
  targetName: string
  result: TransferEligibilityResult | null
  bookingCallback?: boolean
  onChoose: (choice: TransferEligibilityChoice) => void
}) {
  if (!open || !result || result.issues.length === 0) return null

  const headline = primaryTransferEligibilityIssue(result)?.message ?? `${targetName} may be busy.`
  const issues = result.issues

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-warn-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-6">
        <div className="flex items-start gap-3">
          <TransferWarnIcon />
          <div className="min-w-0 flex-1">
            <h2 id="transfer-warn-title" className="text-base font-semibold text-slate-900">
              Transfer may interrupt {targetName}
            </h2>
            <p className="mt-1 text-sm text-slate-600">{headline}</p>
          </div>
        </div>

        {issues.length > 1 && (
          <ul className="mt-3 space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" aria-label="Transfer warnings">
            {issues.map((issue: TransferEligibilityIssue) => (
              <li key={issue.code}>• {issue.message}</li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onChoose('cancel')}
            className="a11y-touch-target w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
          >
            Do not continue
          </button>
          <button
            type="button"
            onClick={() => onChoose('continue')}
            className="a11y-touch-target w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700"
          >
            Continue anyway
          </button>
          <button
            type="button"
            disabled={bookingCallback}
            onClick={() => onChoose('book_callback')}
            className="a11y-touch-target w-full rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-800 transition hover:bg-violet-100 disabled:opacity-60"
          >
            {bookingCallback ? 'Booking callback…' : 'Book a callback'}
          </button>
        </div>
        <p className="mt-3 text-center text-xs text-slate-500">
          Callback adds a telehealth session to the provider&apos;s list — no check-in or journey tracking.
        </p>
      </div>
    </div>
  )
}
